import { Controller } from "@hotwired/stimulus"
import {
  renderPreviewFromMask,
  resetPeriodFromMask,
  segmentsFromMask,
  tokenizeMask
} from "../../utils/number-series-mask.util"

// Connects to data-controller="number-series--form"
export default class extends Controller {
  static targets = [
    "preview",
    "mask",
    "maskPreview",
    "offset",
    "fullYear",
    "fullYearLabel",
    "partYear",
    "noneYear",
    "month",
    "noneMonth",
    "formatCcc",
    "formatCccc",
    "formatCcccc",
    "formatCccccc",
    "formatCcccccc",
    "dashDivider",
    "underscoreDivider",
    "noneDivider",
    "prefix",
    "name",
    "warning",
    "mergedTypeWarning",
    "anchorAlert",
    "anchorTitle",
    "anchorChipList",
    "standaloneAlert",
    "vsDigitAuto",
    "vsDigitAutoLabel",
    "vsDigitNone",
    "segments",
    "metaVs",
    "metaCapacity"
  ]

  // anchorTypes:    { invoice: ["Faktúra", "Daňový doklad", ...], purchase_order: [...] }
  //                 Anchor doc_type → localized labels of every type that falls back to it.
  // standaloneTypes: ["proforma", "quote", "sales_order", "delivery_note"]
  //                 Doc types that have no fallback siblings — picking them shows a neutral info alert.
  // anchorTitleTemplate: "Tento rad obsluhuje %{count} typov dokladov" — interpolated client-side.
  // Source of truth on the Ruby side: NumberSeries::NUMBER_SERIES_FALLBACK_CHAIN + NumberSeriesHelper.
  static values = {
    anchorTypes: { type: Object, default: {} },
    standaloneTypes: { type: Array, default: [] },
    anchorTitleTemplate: { type: String, default: "" },
    // Country-specific { name, prefix } per document_type. Used to re-sync
    // the name + prefix fields when the user changes document_type after
    // initial render. Empty in edit mode (where switching type is rare and
    // we don't want to overwrite user-named series).
    typeDefaults: { type: Object, default: {} }
  }

  // Document types that resolve to the invoice number-series by default (see
  // NumberSeries::NUMBER_SERIES_FALLBACK_CHAIN). Creating an explicit number-series for any
  // of these splits its numbering off the invoice series, which can cause
  // variable-symbol collisions if the new number-series's counter overlaps with
  // the invoice counter — show a soft warning at create time.
  static MERGED_FAMILY_TYPES = [
    "credit_note",
    "advance_credit_note",
    "debit_note",
    "collective_invoice",
    "settlement_invoice"
  ]

  static MAX_VARIABLE_SYMBOL_DIGITS = 10

  // Mirrors NumberSeries::DEFAULT_VS_DIGIT_BY_TYPE on the Ruby side. Drives the
  // "Auto" choice in the VS-digit radio: when picked, the digit baked into the
  // mask follows this table, so client-rendered preview matches what the
  // server seeder would produce for a fresh account. Document types omitted
  // here (quote, expense, delivery_note, sales_order, purchase_order, …) are
  // not payment targets — they fall back to the no-digit ("none") behaviour.
  static DEFAULT_VS_DIGIT_BY_TYPE = {
    invoice: 1,
    credit_note: 2,
    debit_note: 3,
    proforma: 4,
    tax_document: 5,
    advance_credit_note: 6
  }

  // todo: if we want to also support change order of each option we can define mask PYM-N and move these anchors inside string and map values based on that order
  initialize() {
    // Guard flag set while the user is typing directly into the mask field.
    // Prevents rebuildMask() from round-tripping (radios → regenerated mask)
    // and overwriting characters the simplified pane can't represent
    // (mid-token literals, exotic widths, custom divider chars, …).
    this._typingInMask = false
    // During initial load the server-persisted mask is the source of truth —
    // suppress the radio→mask writeback so an exotic saved mask (year-less,
    // 1-2 char counter, custom literals) doesn't get clobbered by whatever the
    // radio defaults happen to be when the form re-hydrates from HTML.
    this._typingInMask = true
    try {
      // Snapshot the server-prefilled name + prefix so we can tell later
      // whether the user customized them. When document_type changes,
      // `#syncDefaultsForDocumentType` only overwrites name/prefix if they
      // still match the previously-applied defaults (or are blank).
      //
      // Must run AFTER `#initializeMaskGenerators()` — the `prefix` input is
      // rendered empty by the server (`text_field_tag "prefix_sequence", nil`)
      // and only gets its initial value when `#initializeMaskGenerators` parses
      // the server-set mask string. Snapshotting before would record `""` as
      // the "applied default" and then refuse to overwrite the populated value
      // on the first document_type change.
      this.#initializeMaskGenerators()
      this._previousDocumentType = document.getElementById("number_series_document_type")?.value || ""
      this._lastAppliedDefaultName = this.hasNameTarget ? this.nameTarget.value : ""
      this._lastAppliedDefaultPrefix = this.hasPrefixTarget ? this.prefixTarget.value : ""
      this.rebuildMask()
    } finally {
      this._typingInMask = false
    }
  }

  // Resolves the auto-VS digit for the currently selected document type.
  // Returns the integer mapped in DEFAULT_VS_DIGIT_BY_TYPE, or null when the
  // type is non-payment (quote/expense/…) and has no mapping.
  #currentAutoDigit() {
    const docType = document.getElementById("number_series_document_type")?.value
    const digit = this.constructor.DEFAULT_VS_DIGIT_BY_TYPE[docType]
    return digit == null ? null : digit
  }

  #initializeMaskGenerators() {
    if (!this.maskTarget.value) return

    const tokens = tokenizeMask(this.maskTarget.value)

    const yearIdx = tokens.findIndex((t) => t.type === "year")
    const counterIdx = tokens.findIndex((t) => t.type === "counter")

    const result = {
      year: "noneYear",
      month: "noneMonth",
      number: "",
      divider: "noneDivider",
      // VS namespace intent: "auto" (mask has `{T}` or a literal digit),
      // "none" (mask has dividers but no digit), or null (no signal — fall
      // back to whichever radio was checked by the server-rendered HTML).
      vsDigit: null
    }

    // Prefix is everything before the first non-literal token (year/month/counter/vs_digit).
    // Multiple leading literal segments are concatenated.
    let prefix = ""
    for (const t of tokens) {
      if (t.type === "literal") prefix += t.value
      else break
    }

    for (const t of tokens) {
      if (t.type === "year") result.year = t.value
      else if (t.type === "month") result.month = "MM"
      else if (t.type === "counter") result.number = t.value
    }

    // VS-namespace signal — detect anywhere in the mask, not just between year
    // and counter. Year is OPTIONAL now, so year-less masks (`{CC}`, `PID-{CCCC}`)
    // also need a VS verdict — otherwise the radio falls back to the
    // server-rendered default ("Auto") and lies about the mask's actual content.
    //
    // - `{T}` token anywhere → vsDigit = "auto"
    // - Any literal digit elsewhere in the mask (legacy shape) → vsDigit = "auto"
    // - Otherwise → vsDigit = "none" (no VS in mask, radio should reflect that)
    const hasVsToken = tokens.some((t) => t.type === "vs_digit_token")
    const hasLiteralDigit = tokens.some((t) => t.type === "literal" && /\d/.test(t.value))
    result.vsDigit = hasVsToken || hasLiteralDigit ? "auto" : "none"

    // Divider character — pulled from the first non-digit literal between year
    // and counter (legacy mask-shape inference).
    if (yearIdx !== -1 && counterIdx !== -1 && counterIdx > yearIdx) {
      const between = tokens.slice(yearIdx + 1, counterIdx)
      const literalText = between
        .filter((t) => t.type === "literal")
        .map((t) => t.value)
        .join("")
      const dividerMatch = literalText.match(/[^0-9]/)
      if (dividerMatch) result.divider = dividerMatch[0]
    }

    this.prefixTarget.value = prefix
    const targets_mapper = {
      YYYY: this.fullYearTarget,
      YY: this.partYearTarget,
      noneYear: this.noneYearTarget,
      MM: this.monthTarget,
      noneMonth: this.noneMonthTarget,
      CCC: this.formatCccTarget,
      CCCC: this.formatCcccTarget,
      CCCCC: this.formatCccccTarget,
      CCCCCC: this.formatCcccccTarget,
      CCCCCCC: this.formatCccccccTarget,
      "-": this.dashDividerTarget,
      _: this.underscoreDividerTarget,
      noneDivider: this.noneDividerTarget
    }
    for (const value of [result.year, result.month, result.number, result.divider]) {
      const target = targets_mapper[value]
      if (target) target.checked = true
    }

    // VS digit radios — two outcomes now (the explicit 0-9 buttons were
    // removed; power users overriding to a non-default digit do so by
    // typing directly into the visible mask field):
    //   * "auto" → check Auto radio (covers `{T}` AND legacy literal-digit
    //     masks; rebuildMask will replace the literal with `{T}` on next
    //     write, harmless because the rendered digit stays the same)
    //   * "none" → check Bez VS radio
    //   * null → leave server-rendered default (Auto)
    if (result.vsDigit === "auto" && this.hasVsDigitAutoTarget) {
      this.vsDigitAutoTarget.checked = true
      if (this.hasVsDigitNoneTarget) this.vsDigitNoneTarget.checked = false
    } else if (result.vsDigit === "none" && this.hasVsDigitNoneTarget) {
      if (this.hasVsDigitAutoTarget) this.vsDigitAutoTarget.checked = false
      this.vsDigitNoneTarget.checked = true
    }
  }

  // Bound to `input` on the visible mask <text_field>. The user is typing
  // directly into the mask string — we re-parse it into the radios so the
  // preview + advanced controls stay consistent. The `_typingInMask` flag
  // suppresses rebuildMask()'s writeback so the user's verbatim characters
  // (including ones the simplified form can't otherwise represent — slash
  // dividers, mid-token literals, …) are preserved.
  handleMaskInput(event) {
    this._typingInMask = true
    try {
      this.#initializeMaskGenerators()
      this.rebuildMask()
    } finally {
      this._typingInMask = false
    }
    // Keep the hidden maskPreview in sync with whatever the user typed.
    const typedMask = event?.target?.value ?? ""
    if (this.hasMaskPreviewTarget) {
      this.maskPreviewTarget.value = typedMask.replace(/[{}]/g, "")
    }
    // Preview + segments re-render from the typed mask (rebuildMask already
    // called #repaintFromMask, so this is here only when this method is
    // invoked outside of rebuildMask — kept for symmetry, no-op redundancy is
    // cheap and avoids drift if the call order ever changes).
    this.#repaintFromMask()
  }

  yearOptions = {
    yyyy: new Date().getFullYear(),
    yy: new Date().getFullYear().toString().slice(-2),
    none: ""
  }

  monthOptions = {
    mm: `0${new Date().getMonth() + 1}`.slice(-2),
    none: ""
  }
  numberOptions = {
    ccc: "001",
    cccc: "0001",
    ccccc: "00001",
    cccccc: "000001",
    ccccccc: "0000001"
  }

  dividerOptions = {
    dash: "-",
    underscore: "_",
    none: ""
  }

  rebuildMask() {
    // Must run BEFORE we read `this.prefixTarget.value` below — when the
    // document_type changed, this swaps in the new country-specific prefix
    // so the regenerated mask reflects the new type.
    this.#syncDefaultsForDocumentType()
    this.#updateVsDigitAutoLabel()
    this.#updateYearLabel()
    const year = this.#setYear()
    const month = this.#setMonth()
    const sequence = this.#setNumber()
    const divider = this.#setDivider()
    const vsDigit = this.#setVsDigit()
    const prefix = this.prefixTarget.value

    // When VS digit is set, the mask sandwiches it between two dividers:
    //   prefix + year + month + divider + digit + divider + counter
    // → renders e.g. `FAK26-1-0001`. Without VS digit we keep the legacy
    // single-divider shape `FAK26-0001` so pre-existing series round-trip
    // unchanged.
    const hasDivider = !!divider.value
    if (vsDigit.value !== "") {
      const secondDivider = hasDivider ? divider.value : ""
      // Mask convention: only VARIABLES get brace-wrapped (`{YY}`, `{T}`,
      // `{CCCC}`). Literal characters (prefix letters, dividers like `-`/`_`)
      // go in bare. So a typical mask reads `FAK{YY}-{T}-{CCCC}` rather than
      // `FAK{YY}{-}{T}{-}{CCCC}`. The Ruby parser accepts both forms but the
      // bare-divider form is what we emit.
      // Skip writeback when the user is typing in the mask field or during
      // initial load — preserves verbatim characters the simplified pane can't
      // represent (`{CC}`, `PID-{CCCC}`, custom dividers, …).
      if (!this._typingInMask) {
        this.maskTarget.value = `${prefix}${this.#wrapValue(year.key)}${this.#wrapValue(month.key)}${divider.key}${this.#wrapValue(vsDigit.key)}${secondDivider}${this.#wrapValue(sequence.key)}`
      }
      this.maskPreviewTarget.value = `${prefix}${year.key}${month.key}${divider.key}${vsDigit.key}${secondDivider}${sequence.key}`
    } else {
      if (!this._typingInMask) {
        this.maskTarget.value = `${prefix}${this.#wrapValue(year.key)}${this.#wrapValue(month.key)}${divider.key}${this.#wrapValue(sequence.key)}`
      }
      this.maskPreviewTarget.value = `${prefix}${year.key}${month.key}${divider.key}${sequence.key}`
    }
    // Preview + segments always render from the actual mask string (the source
    // of truth), not from radio state — so exotic masks render their true
    // output and the radios stay a "preset selector" rather than a source.
    this.#repaintFromMask()
    this.#toggleWarningMessage(this.previewTarget.value)
    this.#renderTypeScopeAlert()
  }

  // When the document_type select changes, swap the name + prefix to the
  // country-specific defaults for the new type — but only when the current
  // values are still the *previous* defaults (or blank). If the user typed
  // a custom name like "Faktúry pre Acme s.r.o." we leave it alone.
  //
  // Called at the top of `rebuildMask` so the prefix is updated before the
  // mask is regenerated. Edit mode passes `typeDefaults = {}`, which makes
  // this a no-op — switching type on a persisted series shouldn't rewrite
  // the user's existing name.
  #syncDefaultsForDocumentType() {
    const docType = document.getElementById("number_series_document_type")?.value || ""
    if (!docType || docType === this._previousDocumentType) return

    const defaults = this.typeDefaultsValue?.[docType]
    if (!defaults) {
      this._previousDocumentType = docType
      return
    }

    if (this.hasNameTarget) {
      const current = this.nameTarget.value
      if (current === "" || current === this._lastAppliedDefaultName) {
        this.nameTarget.value = defaults.name
        this._lastAppliedDefaultName = defaults.name
      }
    }
    if (this.hasPrefixTarget) {
      const current = this.prefixTarget.value
      if (current === "" || current === this._lastAppliedDefaultPrefix) {
        this.prefixTarget.value = defaults.prefix
        this._lastAppliedDefaultPrefix = defaults.prefix
      }
    }
    this._previousDocumentType = docType
  }

  // Single source of truth for the visible preview — reads `maskTarget.value`
  // and paints the flat preview string, the colored-segments breakdown, the
  // meta VS digits, and the capacity hint. Replaces an earlier radio-driven
  // path that lied about exotic masks (`{CC}`, year-less, custom literals)
  // because radio state didn't always cover what the user typed.
  //
  // Called from `rebuildMask` (after the radio→mask writeback) and from
  // `handleMaskInput` (when the user types directly into the mask field). In
  // both cases `maskTarget.value` is current and authoritative.
  #repaintFromMask() {
    const mask = this.maskTarget.value
    const autoDigit = this.#currentAutoDigit()
    const offset = this.hasOffsetTarget ? this.offsetTarget.value || 1 : 1

    if (this.hasPreviewTarget) {
      this.previewTarget.value = mask ? renderPreviewFromMask(mask, { autoDigit, offset }) : ""
    }

    if (this.hasSegmentsTarget) {
      const host = this.segmentsTarget
      const labels = {
        prefix: host.dataset.segmentLabelPrefix || "prefix",
        year: host.dataset.segmentLabelYear || "year",
        month: host.dataset.segmentLabelMonth || "month",
        vs: host.dataset.segmentLabelVs || "VS",
        counter: host.dataset.segmentLabelCounter || "counter"
      }
      const segs = segmentsFromMask(mask, { autoDigit, offset, labels })
      host.innerHTML = segs
        .map((s) => {
          const labelHtml = s.label ? this.#escapeHtml(s.label) : "&nbsp;"
          return `<span class="number-series-segments__seg number-series-segments__seg--${s.kind}"><span class="number-series-segments__value">${this.#escapeHtml(s.value)}</span><span class="number-series-segments__label">${labelHtml}</span></span>`
        })
        .join("")
    }

    if (this.hasMetaVsTarget) {
      this.metaVsTarget.textContent = (this.previewTarget?.value || "").replace(/\D/g, "")
    }

    if (this.hasMetaCapacityTarget && this.hasSegmentsTarget) {
      // Capacity = 10^counterWidth - 1 (e.g. CCCC → 9999). Derive directly from
      // the mask's counter token so exotic widths (CC, CCCCCCCC) render their
      // actual capacity instead of the radio default.
      const tokens = tokenizeMask(mask)
      const counterToken = tokens.find((t) => t.type === "counter")
      const counterWidth = counterToken ? counterToken.value.length : 0
      const max = counterWidth > 0 ? 10 ** counterWidth - 1 : 0
      const formatted = max.toLocaleString("sk-SK")
      // The unit follows the counter's RESET period, which the mask decides:
      // the server resets the counter whenever the rendered prefix-before-counter
      // changes (NumberSeries#reset_if_period_changed). A {MM} token there makes
      // that prefix change every month → "per month"; a year-only prefix changes
      // it every year → "per year"; a mask with neither never resets → lifetime
      // total. A year after the counter ({CCCC}-{YY}) is a suffix and resets
      // nothing. Hardcoding "/ year" here mislabels month and permanent-id series.
      const ds = this.segmentsTarget.dataset
      const templates = {
        month: ds.segmentCapacityTemplateMonth,
        year: ds.segmentCapacityTemplateYear,
        none: ds.segmentCapacityTemplateTotal
      }
      const template = templates[resetPeriodFromMask(mask)]
      this.metaCapacityTarget.textContent = (template || "{{count}}").replace("{{count}}", formatted)
    }
  }

  // Reflect the server-side year_constructor offsets in the YYYY radio
  // label, so a user picking a proforma series with NO VS digit sees
  // `3026` on the button (= year + 1000) and understands the offset is
  // what isolates the proforma variable-symbol range from the invoice
  // one. With a VS digit set the namespace already disambiguates so the
  // legacy +1000 hack is suppressed — show the plain year.
  #updateYearLabel() {
    if (!this.hasFullYearLabelTarget) return
    const docType = document.getElementById("number_series_document_type")?.value
    const baseYear = this.yearOptions.yyyy
    const usingVsDigit = this.#resolvedVsDigitValue() !== ""
    const offset = !usingVsDigit && docType === "proforma" ? 1000 : 0
    this.fullYearLabelTarget.textContent = baseYear + offset
  }

  // Refreshes the "Auto" radio label so the user always sees which digit will
  // be baked into the mask for the current document_type, e.g.
  // "Auto · 4 (proforma)" or "Auto · bez VS" for non-payment types.
  #updateVsDigitAutoLabel() {
    if (!this.hasVsDigitAutoLabelTarget) return
    const docType = document.getElementById("number_series_document_type")?.value
    const defaultDigit = this.constructor.DEFAULT_VS_DIGIT_BY_TYPE[docType]
    if (defaultDigit == null) {
      this.vsDigitAutoLabelTarget.textContent = "Auto"
    } else {
      this.vsDigitAutoLabelTarget.textContent = `Auto · ${defaultDigit}`
    }
  }

  #combineOffsetWithNumber(offSet, number_preview) {
    if (!offSet || offSet.length > number_preview.length) {
      this.offsetTarget.value = ""
      return number_preview.toString()
    } else {
      const lengthOffset = offSet.length
      return number_preview.slice(0, number_preview.length - lengthOffset) + offSet
    }
  }

  #wrapValue(value) {
    if (value) {
      return `{${value}}`
    } else {
      return ""
    }
  }

  #setYear() {
    if (this.fullYearTarget.checked) {
      // Optional chaining mirrors `#currentAutoDigit` and `renderPreviewFromMask` —
      // resilient if the document_type input is ever absent from the DOM.
      const docType = document.getElementById("number_series_document_type")?.value
      const usingVsDigit = this.#resolvedVsDigitValue() !== ""
      // The legacy +1000 / +3000 offsets in year_constructor on the Ruby
      // side only fire for masks without a VS-digit namespace (see
      // NumberSeries#legacy_year_with_type_offset). Mirror that gate here
      // so the preview the user sees in the form matches what the server
      // will persist.
      const applyLegacyOffset = !usingVsDigit && docType === "proforma"
      return {
        key: "YYYY",
        value: applyLegacyOffset ? this.yearOptions.yyyy + 1000 : this.yearOptions.yyyy
      }
    } else if (this.partYearTarget.checked) {
      return { key: "YY", value: this.yearOptions.yy }
    } else if (this.noneYearTarget.checked) {
      return { key: "", value: this.yearOptions.none }
    }
  }

  // VS digit radio has two states: Auto and Bez VS (None).
  //
  //   - Auto → mask gets the `{T}` placeholder; preview shows the digit
  //     resolved from DEFAULT_VS_DIGIT_BY_TYPE[document_type]. The mask
  //     carries intent rather than a baked digit so future ISDOC updates
  //     propagate automatically (see NumberSeries#format_constructor's
  //     :vs_digit_token branch).
  //   - None → mask omits any namespacing; preview shows nothing in the
  //     VS slot. Falls back to legacy single-divider shape.
  //
  // For document types that have no canonical digit (quote, expense,
  // delivery_note, sales_order, purchase_order), "Auto" still emits `{T}`
  // but the server renders it as an empty string at format time. The
  // preview here mirrors that: empty value when no default digit exists.
  #setVsDigit() {
    if (this.hasVsDigitNoneTarget && this.vsDigitNoneTarget.checked) {
      return { key: "", value: "" }
    }
    if (this.hasVsDigitAutoTarget && this.vsDigitAutoTarget.checked) {
      const digit = this.#resolvedVsDigitValue()
      // key = "T" so #wrapValue("T") produces `{T}` (the Type-code token) in the mask
      // value = the rendered digit for the preview
      return digit === "" ? { key: "", value: "" } : { key: "T", value: digit }
    }
    return { key: "", value: "" }
  }

  // Resolves the digit that "Auto" produces for the current document_type.
  // Returns "" when the type has no canonical digit (which renders the
  // mask without namespacing even though {T} is technically present).
  #resolvedVsDigitValue() {
    if (this.hasVsDigitNoneTarget && this.vsDigitNoneTarget.checked) return ""
    if (this.hasVsDigitAutoTarget && this.vsDigitAutoTarget.checked) {
      const docType = document.getElementById("number_series_document_type")?.value
      const digit = this.constructor.DEFAULT_VS_DIGIT_BY_TYPE[docType]
      return digit == null ? "" : String(digit)
    }
    return ""
  }

  #setMonth() {
    if (this.monthTarget.checked) {
      return { key: "MM", value: this.monthOptions.mm }
    } else if (this.noneMonthTarget.checked) {
      return { key: "", value: this.yearOptions.none }
    }
  }

  #setNumber() {
    if (this.formatCccTarget.checked) {
      return { key: "CCC", value: this.numberOptions.ccc }
    } else if (this.formatCcccTarget.checked) {
      return { key: "CCCC", value: this.numberOptions.cccc }
    } else if (this.formatCccccTarget.checked) {
      return { key: "CCCCC", value: this.numberOptions.ccccc }
    } else if (this.formatCcccccTarget.checked) {
      return { key: "CCCCCC", value: this.numberOptions.cccccc }
    } else if (this.formatCccccccTarget.checked) {
      return { key: "CCCCCCC", value: this.numberOptions.ccccccc }
    }
  }

  #setDivider() {
    if (this.dashDividerTarget.checked) {
      return { key: "-", value: this.dividerOptions.dash }
    } else if (this.underscoreDividerTarget.checked) {
      return { key: "_", value: this.dividerOptions.underscore }
    } else if (this.noneDividerTarget.checked) {
      return { key: "", value: this.dividerOptions.none }
    }
  }

  #toggleWarningMessage(sequencePreviewValue) {
    if (!this.hasWarningTarget) {
      return
    }

    const variableSymbolFormat = sequencePreviewValue ? sequencePreviewValue.replace(/\D/g, "") : ""

    if (variableSymbolFormat.length > this.constructor.MAX_VARIABLE_SYMBOL_DIGITS) {
      this.warningTarget.classList.remove("d-none")
    } else {
      this.warningTarget.classList.add("d-none")
    }
  }

  // Renders one of three mutually exclusive alerts under the document_type
  // select, based on which family role the picked type plays:
  //   - anchor (invoice / purchase_order) → green: lists every type covered by this sequence
  //   - merged-family member (credit_note, debit_note, …) → existing soft warning
  //   - clearly-standalone (proforma, quote, …) → neutral info
  // Other types (rent_income, expense) show no alert, matching the previous
  // behaviour where only MERGED_FAMILY_TYPES surfaced anything.
  // (tax_document and recurring_document are excluded from the dropdown
  // entirely — see Document.document_types_select_options.)
  #renderTypeScopeAlert() {
    const documentTypeSelect = document.getElementById("number_series_document_type")
    const documentType = documentTypeSelect ? documentTypeSelect.value : ""

    // Stimulus throws on `this.xTarget` if the target isn't found, so guard
    // each de-reference with `hasXTarget` before passing to `#hideAlert`.
    if (this.hasAnchorAlertTarget) this.#hideAlert(this.anchorAlertTarget)
    if (this.hasMergedTypeWarningTarget) this.#hideAlert(this.mergedTypeWarningTarget)
    if (this.hasStandaloneAlertTarget) this.#hideAlert(this.standaloneAlertTarget)

    const coveredLabels = this.anchorTypesValue?.[documentType]
    if (coveredLabels && this.hasAnchorAlertTarget) {
      this.#renderAnchorAlert(coveredLabels)
    } else if (this.constructor.MERGED_FAMILY_TYPES.includes(documentType) && this.hasMergedTypeWarningTarget) {
      this.mergedTypeWarningTarget.classList.remove("d-none")
    } else if (this.standaloneTypesValue.includes(documentType) && this.hasStandaloneAlertTarget) {
      this.standaloneAlertTarget.classList.remove("d-none")
    }
  }

  #renderAnchorAlert(coveredLabels) {
    if (this.hasAnchorTitleTarget) {
      this.anchorTitleTarget.textContent = this.anchorTitleTemplateValue.replace("%{count}", coveredLabels.length)
    }
    if (this.hasAnchorChipListTarget) {
      this.anchorChipListTarget.innerHTML = coveredLabels
        .map(
          (label) =>
            `<span class="badge bg-success-subtle text-success-emphasis border border-success-subtle fw-normal"><span class="material-symbols-outlined align-middle" style="font-size: 12px;">description</span>&nbsp;${this.#escapeHtml(label)}</span>`
        )
        .join("")
    }
    this.anchorAlertTarget.classList.remove("d-none")
  }

  #hideAlert(target) {
    target?.classList.add("d-none")
  }

  // Defensive escape — locale strings are server-rendered and trusted, but
  // the chip labels round-trip through JS, so any future change that pipes
  // unexpected input through here won't open an injection.
  #escapeHtml(str) {
    if (!str) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }
}
